import {
  DataTypeLocator,
  FieldDataTypeConfig,
  InputValueContentNode,
  QueryConfig,
  QueryNode,
  QueryNodeUtils,
} from '@helo/cms'
import { Assert, Debug, If, Value, asyncPipe, pipe, tap } from '@helo/utils'
import { StateCreator } from 'zustand'

import { AuthSlice } from '../auth/authSlice'
import { CmsSlice } from '../cms'
import { PersistenceSlice } from '../persistence/persistenceSlice'
import { ViewSlice } from '../view/viewSlice'
import { QuerySliceActions, UpdateQueryOptionsV2 } from './querySlice.actions'
import { findDataTypeConfig, getQuery } from './querySlice.parts'

export type PageQueryConfigOptions = {
  persistToQueryString?: boolean
} & QueryConfig

export type QueryStore = {
  queries: Record<string, QueryNode>

  // resetState: () => void
  registerQuery: (options: PageQueryConfigOptions) => void
  updateQuery: (options: UpdateQueryOptionsV2) => Promise<QueryNode>
  updateVariables: (
    queryId: string,
    variables: Record<string, unknown>,
  ) => Promise<QueryNode>
  updateQueryInputValue: (
    queryId: string,
    inputValueContentNode: InputValueContentNode | InputValueContentNode[],
  ) => Promise<QueryNode>
  refetchQuery: (queryId: string) => Promise<QueryNode | undefined>
  getQueryAndWaitForResult: (queryId: string) => Promise<QueryNode | undefined>
  resetPagination: (queryId: string) => void

  removeQuery: (queryId: string) => void
  getQuery: (queryId: string) => QueryNode | undefined
  removeAllQueries: () => void
  getQueryDefinition: (
    querySelector: DataTypeLocator,
  ) => FieldDataTypeConfig | undefined
}
export type QuerySlice = {
  queryStore: QueryStore
}

export const querySlice: StateCreator<
  QuerySlice & AuthSlice & ViewSlice & PersistenceSlice & CmsSlice,
  [],
  [],
  QuerySlice
> = (set, get, storeApi) => ({
  queryStore: {
    queries: {},
    registerQuery: options =>
      pipe(get(), QuerySliceActions.registerQuery(options, storeApi), tap(set)),
    getQueryDefinition: (querySelector: DataTypeLocator) =>
      pipe(get(), findDataTypeConfig(querySelector)),
    updateQuery: options =>
      asyncPipe(
        get(),
        QuerySliceActions.updateQuery(options),
        tap(store => set(store)),
        getQuery(options.queryId),
        QueryNodeUtils.waitForResult(get().cms.baseDataTypeConfigs),
      ),
    updateQueryInputValue: async (
      queryId: string,
      inputValueContentNode: InputValueContentNode | InputValueContentNode[],
    ) => {
      return asyncPipe(
        get(),
        QuerySliceActions.updateQueryInputValue(
          queryId,
          inputValueContentNode,
          storeApi,
        ),
        tap(store => set(store)),
        getQuery(queryId),
        Assert.orThrow(
          Value.isNotNullable,
          () => new Error('Query node not found'),
        ),
        If.then(
          queryNode => queryNode.paginated,
          queryNode => asyncPipe.return(queryNode as QueryNode),
        ),
        QueryNodeUtils.waitForResult(get().cms.baseDataTypeConfigs),
      )
    },

    updateVariables: (queryId: string, variables: Record<string, unknown>) =>
      asyncPipe(
        get(),
        QuerySliceActions.updateVariables(queryId, variables, storeApi),
        tap(store => set(store)),
        getQuery(queryId),
        queryNode => {
          if (queryNode?.paginated) {
            return queryNode
          }
          return QueryNodeUtils.waitForResult(get().cms.baseDataTypeConfigs)(
            queryNode,
          )
        },
      ),
    refetchQuery: queryId =>
      pipe(
        get(),
        QuerySliceActions.refetchQuery(queryId, storeApi),
        // tap(store => set(store)),
        // store => get(),
        // getQuery(queryId),
        QueryNodeUtils.waitForResult(get().cms.baseDataTypeConfigs),
      ),
    getQueryAndWaitForResult: queryId =>
      asyncPipe(get(), getQuery(queryId), queryNode => {
        if (!queryNode) return undefined
        return QueryNodeUtils.waitForResult(get().cms.baseDataTypeConfigs)(
          queryNode,
        )
      }),
    resetPagination: queryId =>
      pipe(
        get(),
        QuerySliceActions.resetPagination(queryId, storeApi),
        tap(store => set(store)),
      ),
    removeQuery: queryId =>
      pipe(
        get(),
        QuerySliceActions.removeQuery(queryId),
        tap(store => set(store)),
      ),
    getQuery: queryId => pipe(get(), getQuery(queryId)),
    removeAllQueries: () =>
      pipe(
        get(),
        QuerySliceActions.removeAllQueries(),
        tap(store => set(store)),
      ),
  },
})
